import { Check } from 'lucide-react'
import { useTemplates } from '@/hooks/useTemplates'

const CATEGORY_LABELS: Record<string, string> = {
  hero: 'Hero', features: 'Features', cta: 'CTA',
  saas: 'SaaS', portfolio: 'Portfolio', about: 'About',
  landing: 'Landing', blog: 'Blog',
}

const CATEGORY_COLORS: Record<string, string> = {
  saas: 'bg-violet-100 text-violet-700',
  portfolio: 'bg-blue-100 text-blue-700',
  about: 'bg-green-100 text-green-700',
  landing: 'bg-amber-100 text-amber-700',
  blog: 'bg-pink-100 text-pink-700',
  hero: 'bg-gray-100 text-gray-600',
  features: 'bg-gray-100 text-gray-600',
  cta: 'bg-gray-100 text-gray-600',
}

export function TemplatePicker({
  selected,
  onSelect,
}: {
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  const { data: templates = [], isLoading } = useTemplates()

  if (isLoading) return (
    <div className="flex justify-center py-4">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
    </div>
  )

  if (!templates.length) return null

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-gray-700">
        Start from a template <span className="text-gray-400 font-normal">(optional)</span>
      </p>
      <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
        {templates.map((tpl) => {
          const isSelected = selected === tpl.id
          const colorClass = CATEGORY_COLORS[tpl.category] ?? 'bg-gray-100 text-gray-600'
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => onSelect(isSelected ? null : tpl.id)}
              className={[
                'relative flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-all cursor-pointer',
                isSelected
                  ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500/30'
                  : 'border-gray-200 bg-white hover:border-violet-300',
              ].join(' ')}
            >
              {isSelected && (
                <span className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-violet-600">
                  <Check className="h-3 w-3 text-white" />
                </span>
              )}
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colorClass}`}>
                {CATEGORY_LABELS[tpl.category] ?? tpl.category}
              </span>
              <span className="text-sm font-semibold text-gray-900 leading-snug">{tpl.name}</span>
              {tpl.description && (
                <span className="text-xs text-gray-400 line-clamp-2">{tpl.description}</span>
              )}
              <span className="text-xs text-gray-300 mt-auto">
                {tpl.schema.blocks.length} block{tpl.schema.blocks.length !== 1 ? 's' : ''}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
